// src/pages/ProductDetailPage/ProductDetailPage.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { FaUndoAlt, FaMedal, FaThumbsUp, FaShippingFast, FaHeart } from "react-icons/fa";
import { MdPolicy } from "react-icons/md";
import { ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "react-toastify";
import pincodes from "../../data/serviceablePincodes.json";
import "./ProductDetailPage.css";
import Footer from "../../components/Footer/Footer";
import { API } from "../../services/api";
import { useCart } from "../../contextPage/CartContext";
import { useUser } from "../../contextPage/UserContext";


const PLACEHOLDER_IMG = "/placeholder.jpg";
const formatINR = (n) => {
    const v = Number.parseFloat(n);
    if (Number.isNaN(v)) return "—";
    return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
};

export default function ProductDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();

    // get latest cart + addToCart
    const { addToCart, cart, loadCart } = useCart();
    const { isLoggedIn, loadingUser, reloadUser } = useUser();

    const [loading, setLoading] = useState(true);
    const [errMsg, setErrMsg] = useState("");
    const [product, setProduct] = useState(null);
    const [images, setImages] = useState([]);
    const [mainImage, setMainImage] = useState(PLACEHOLDER_IMG);
    const [quantity, setQuantity] = useState(1);
    const [showDetails, setShowDetails] = useState(false);
    const [showFullDesc, setShowFullDesc] = useState(false);
    const [descOverflows, setDescOverflows] = useState(false);
    const descRef = useRef(null);

    const [pincode, setPincode] = useState("");
    const [status, setStatus] = useState("");
    const [selectedVariant, setSelectedVariant] = useState(null);

    const promiseItems = useMemo(
        () => [
            { icon: <FaUndoAlt />, text: "15 Day Returnable" },
            { icon: <FaMedal />, text: "Certified Jewellery" },
            { icon: <FaThumbsUp />, text: "100% Refund" },
            { icon: <FaShippingFast />, text: "Free Shipping" },
            { icon: <FaHeart />, text: "Free Returns" },
            { icon: <MdPolicy />, text: "Buy-Back Policy" },
        ],
        []
    );

    // Fetch product by id
    useEffect(() => {
        let mounted = true;
        setLoading(true);
        setErrMsg("");

        fetch(API.getProductById(id))
            .then(async (res) => {
                if (!res.ok) {
                    const t = await res.text().catch(() => "");
                    throw new Error(t || `Failed to fetch product (status ${res.status})`);
                }
                return res.json();
            })
            .then((data) => {

                if (!mounted) return;
                setProduct(data);
                if (Array.isArray(data.variants) && data.variants.length > 0) {
                    const firstWithSize = data.variants.find(
                        (v) => v?.size && String(v.size).trim() !== ""
                    );
                    setSelectedVariant(firstWithSize || null);
                }
                let imgs = [];
                try {
                    imgs = Array.isArray(data.images) ? data.images : JSON.parse(data.images || "[]");
                } catch {
                    /* ignore bad JSON */
                }
                setImages(imgs);
                setMainImage(imgs.length ? `${API.imagePath}${imgs[0]}` : PLACEHOLDER_IMG);
            })
            .catch((e) => {
                if (!mounted) return;
                console.error("Failed to fetch product:", e);
                setErrMsg("Failed to load product. Please try again later.");
            })
            .finally(() => mounted && setLoading(false));

        return () => {
            mounted = false;
        };
    }, [id]);





    useEffect(() => {
       const el = descRef.current;
            if (!el) return;
            const check = () => {
            // force clamp class while checking
                   const had = el.classList.contains("clamped");
                 el.classList.add("clamped");
                  const needs = el.scrollHeight - 1 > el.clientHeight; // -1 avoids jitter
                  setDescOverflows(needs);
                  if (!had && !showFullDesc) el.classList.add("clamped");
                  if (showFullDesc) el.classList.remove("clamped");
                };
            check();
            window.addEventListener("resize", check);
            return () => window.removeEventListener("resize", check);
          }, [product?.detailedDescription, showFullDesc]);

    const increment = () => {
        if (quantity < availableStock) {
            setQuantity((q) => q + 1);
        } else {
            toast.info(`Only ${availableStock} item(s) left in stock`);
        }
    };
    const decrement = () => setQuantity((q) => (q > 1 ? q - 1 : 1));

    // const handleAddToCart = async () => {
    //     if (loadingUser) return;

    //     // try silent restore if we have a token but context isn't populated yet
    //     if (!isLoggedIn) {
    //         const token = localStorage.getItem("token");
    //         if (token && typeof reloadUser === "function") {
    //             await reloadUser();
    //         }
    //     }

    //     if (!isLoggedIn) {
    //         toast.info("Please login to add products to cart");
    //         navigate("/login");
    //         return;
    //     }

    //     // ensure we have the freshest cart before deciding whether to add +1
    //     if (typeof loadCart === "function") {
    //         await loadCart();
    //     }

    //     // If already in cart -> add +1; else add selected quantity
    //     const pid = String(product.id);
    //     const line = (cart || []).find(
    //         (i) => String(i.product_id ?? i.productId ?? i.id) === pid
    //     );
    //     const qtyToAdd = line ? 1 : quantity;

    //     const res = await addToCart(product.id, quantity, selectedVariant?.id || null);
    //     const ok = res?.ok ?? res === true;
    //     const unauthorized = res?.unauthorized;

    //     if (ok) {
    //         if (line) toast.success("Quantity increased by 1");
    //         navigate("/cart");
    //     } else if (unauthorized) {
    //         toast.info("Session expired. Please login again.");
    //         navigate("/login");
    //     } else {
    //         toast.error("Failed to add to cart. Please try again.");
    //     }
    // };
    const handleAddToCart = async () => {
        if (loadingUser) return;

        // Restore session if needed
        if (!isLoggedIn) {
            const token = localStorage.getItem("token");
            if (token && typeof reloadUser === "function") {
                await reloadUser();
            }
        }

        if (!isLoggedIn) {
            toast.info("Please login to add products to cart");
            navigate("/login");
            return;
        }

        // Require size if variants exist
        if (product.variants?.length > 0 && !selectedVariant) {
            toast.info("Please select a size");
            return;
        }

        const qtyToAdd = quantity;

        const unitPrice =
            selectedVariant?.price != null
                ? Number(selectedVariant.price)
                : product.variants?.length > 0
                ? Number(product.variants[0].price || 0)
                : Number(product.price || product.displayPrice || 0);
        let imgs = [];
        try {
            imgs = Array.isArray(product.images) ? product.images : JSON.parse(product.images || "[]");
        } catch {
            imgs = [];
        }
        const snapshot = {
            productName: product.productName ?? product.name,
            unitPrice: Number(unitPrice) || 0,
            images: imgs,
        };

        const res = await addToCart(product.id, qtyToAdd, selectedVariant?.id || null, snapshot);

        const ok = res?.ok ?? res === true;
        const unauthorized = res?.unauthorized;

        if (res?.alreadyInCart) {
            toast.info("This product is already in your cart");
            return;
        }

        if (ok) {
            toast.success("Added to cart");
            navigate("/cart");
        } else if (unauthorized) {
            toast.info("Session expired. Please login again.");
            navigate("/login");
        } else {
            toast.error("Failed to add to cart. Please try again.");
        }
    };
    const handleBuyNow = () => {
        navigate("/buy-now", { state: { product, quantity } });
    };
    // const handleBuyNow1 = () => {
    //     navigate("/buy-now", { state: { product, quantity } });
    // };
const handleBuyNow1 = () => {
    if (!selectedVariant) {
        toast.info("Please select a size");
        return;
    }

    navigate("/buy-now", {
        state: {
            product,
            quantity,
            variant_id: selectedVariant.id,
            price: selectedVariant.price
        }
    });
};
    if (loading) return <p style={{ padding: 16 }}>Loading product details...</p>;
    if (errMsg) return <p style={{ padding: 16, color: "crimson" }}>{errMsg}</p>;
    if (!product) return <p style={{ padding: 16 }}>Product not found.</p>;

    // Treat discountcost as a percentage off (e.g., 15 means 15% off)
    const costValue = selectedVariant?.price != null
        ? Number(selectedVariant.price)
        : (product.variants?.length > 0
            ? Number(product.variants[0].price || 0)
            : Number(product.price || product.displayPrice || 0));

    const hasVariants = Array.isArray(product.variants) && product.variants.length > 0;

    // Total stock used to decide overall "Out of Stock" vs available
    const totalStock = hasVariants
        ? product.variants.reduce(
            (sum, v) => sum + Number(v?.quantity || 0),
            0
        )
        : Number(product.quantity || 0);

    // Per-selection stock:
    // - for variant products, use the selected variant's quantity
    // - for simple products (no variants), use totalStock (product.quantity)
    const availableStock = hasVariants
        ? (selectedVariant ? Number(selectedVariant.quantity || 0) : 0)
        : totalStock;

    const isOutOfStock = totalStock <= 0;
    const discountPct = Number(product.discountcost) || 0;

    const sizeableVariants = Array.isArray(product.variants)
        ? product.variants.filter((v) => {
              const val = v?.size;
              return val != null && String(val).trim() !== "";
          })
        : [];

    const discountedValue = Math.max(
        0,
        Math.round(costValue * (1 - discountPct / 100))
    );

    // Display strings
    const cost = formatINR(costValue);          // original price
    const mrp = formatINR(discountedValue);    // final (discounted) price

    const youSave = Math.max(0, costValue - discountedValue);

    const hasDiscount = discountPct > 0;
    const serviceableSet = new Set(pincodes);
    const checkPincode = () => {
        if (pincode.length !== 6) {
            alert("Please enter valid 6 digit pincode");
            return;
        }

        if (serviceableSet.has(pincode)) {
            setStatus("success");
        } else {
            setStatus("error");
        }
    };

    const whatsappNumber = "917204429777"; // 👉 Replace with your number

    const whatsappMessage = `
Hello AVNII Jewellers 👋

I am interested in this product:

🛍 Product: ${product.productName}
💰 Price: ₹${costValue}
📏 Size: ${selectedVariant?.size || "N/A"}
📍 My Pincode: ${pincode}

Please confirm delivery availability.

Product Link:
${window.location.href}
`;

    const whatsappURL = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
        whatsappMessage
    )}`;


    
    return (
        <>
            <div className="product-detail container">
                {/* LEFT SECTION: Images */}
                <div className="left-section">
                    {/* <img
                        src={mainImage}
                        alt={product.productName}
                        className="main-product-image"
                        onError={(e) => (e.currentTarget.src = PLACEHOLDER_IMG)}
                    /> */}
                    <img
                        src={mainImage}
                        alt={product.productName}
                        className={`main-product-image ${isOutOfStock ? "out-stock-img" : ""}`}
                        onError={(e) => (e.currentTarget.src = PLACEHOLDER_IMG)}
                    />
                    {isOutOfStock && (
                        <div className="out-stock-overlay">
                            OUT OF STOCK
                        </div>
                    )}
                    <div className="thumbnail-container">
                        {images.map((img, i) => {
                            const src = `${API.imagePath}${img}`;
                            return (
                                <img
                                    key={i}
                                    src={src}
                                    alt={`thumb-${i}`}
                                    className={`thumbnail ${mainImage === src ? "active" : ""}`}
                                    onClick={() => setMainImage(src)}
                                    onError={(e) => (e.currentTarget.src = PLACEHOLDER_IMG)}
                                />
                            );
                        })}
                    </div>

                  
                </div>

                {/* RIGHT SECTION: Info */}
                <div className="right-section">
                    <h2 className="product-name">{product.productName}</h2>

                    <div className="price-block">
                        {product.size && (
                            <p>
                                SIZE: 
                                <a
                                    className="size-guide-link"
                                    href="/size.pdf"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ marginLeft: "8px" }}
                                >
                                    Size Guide
                                </a>
                            </p>
                        )}

                        {/* ✅ ONLY show striked price if discount exists */}
                        {hasDiscount && (
                            <p className="mrp-striked">₹{cost}</p>
                        )}

                        {/* ✅ Final price (always shown) */}
                        <p className="final-price">
                            ₹{hasDiscount ? mrp : cost}
                        </p>

                        {/* ✅ Offer banner only if discount exists */}
                        {hasDiscount && (
                            <span className="discount-label">
                                <strong>{discountPct}% OFF</strong> — Save ₹{formatINR(youSave)} Today
                            </span>
                        )}
                    </div>
                    {sizeableVariants.length > 0 && (
                        <div className="size-selector">
                            <p className="size-label">
                                Select Size
                                {(() => {
                                    const sizeType =
                                        selectedVariant?.sizeType ||
                                        selectedVariant?.size_type ||
                                        product.variants?.[0]?.sizeType ||
                                        product.variants?.[0]?.size_type ||
                                        product.sizeType ||
                                        product.size_type;
                                    const val = sizeType ? String(sizeType).trim() : "";
                                    if (!val || val.toLowerCase() === "plain") return null;
                                    return <> ({val})</>;
                                })()}
                                :
                            </p>
                            <div className="size-options">
                                {sizeableVariants.map((variant) => (
                                    <button
                                        disabled={variant.quantity === 0}
                                        style={{
                                            opacity: variant.quantity === 0 ? 0.4 : 1,
                                            cursor: variant.quantity === 0 ? "not-allowed" : "pointer"
                                        }}
                                        key={variant.id}
                                        className={`size-btn ${selectedVariant?.id === variant.id ? "active" : ""
                                            }`}
                                        onClick={() => {
                                            setSelectedVariant(variant);
                                            setQuantity(1); // reset qty
                                        }}
                                    >
                                        {variant.size}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* pincode dummy UI */}
                    {/* <div className="pincode-section">
                        <input type="text" placeholder="Your pincode" />
                        <button>Update</button>
                        <p className="pincode-hint">Provide pincode for delivery date & nearby stores!</p>
                    </div> */}
                    <div className="pincode-section">
                        <input
                            type="text"
                            placeholder="Enter your pincode"
                            maxLength={6}
                            value={pincode}
                            onChange={(e) =>
                                setPincode(e.target.value.replace(/\D/g, ""))
                            }
                        />

                        <button onClick={checkPincode}>Check</button>

                        {status === "success" && (
                            <p style={{ color: "green", marginTop: "6px" }}>
                                ✅ Delivery available at this pincode
                            </p>
                        )}

                        {status === "error" && (
                            <p style={{ color: "red", marginTop: "6px" }}>
                                ❌ This pincode is not deliverable.
                                <br />
                                <a
                                    href={whatsappURL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: "#25D366", fontWeight: "600" }}
                                >
                                    Contact us on WhatsApp →
                                </a>
                            </p>
                        )}
                    </div>
                    <div className="product-options">
                        {/* <div className="option-group quantity-group">
                            <label>Quantity:</label>
                            <div className="quantity-control">
                                <button onClick={decrement} aria-label="Decrease quantity">
                                    -
                                </button>
                                <span>{quantity}</span>
                                <button onClick={increment} aria-label="Increase quantity">
                                    +
                                </button>
                            </div>
                        </div> */}
                        <div className="quantity-control">
                            <button onClick={decrement} aria-label="Decrease quantity">
                                -
                            </button>

                            <span>{quantity}</span>

                            <button
                                onClick={increment}
                                aria-label="Increase quantity"
                                disabled={quantity >= availableStock}
                                style={{
                                    opacity: quantity >= availableStock ? 0.5 : 1,
                                    cursor: quantity >= availableStock ? "not-allowed" : "pointer"
                                }}
                            >
                                +
                            </button>
                        </div>
                        {availableStock > 0 ? (
                            <p style={{ color: "#444", fontSize: "14px", marginTop: "6px" }}>
                                {availableStock <= 5 && (
                                    <span style={{ color: "crimson", fontWeight: "600" }}>
                                        ⚠ Only {availableStock} left in stock
                                    </span>
                                )}
                            </p>
                        ) : (
                            <p style={{ color: "crimson", fontWeight: "600", textAlign:"left" }}>
                                ❌ Out of Stock
                            </p>
                        )}




                        <div className="promise-container">
                            <h4>AVNII PROMISE</h4>
                            <div className="promise-items">
                                {promiseItems.map((item, index) => (
                                    <div className="promise-item" key={index}>
                                        <span className="promise-icon">{item.icon}</span>
                                        <span className="promise-text">{item.text}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="certifications">
                        <span>CERTIFIED BY</span>
                        <div className="cert-logos">BIS hallmarked with HUID</div>
                    </div>
                    {/* Buttons */}
                    <div className="button-row">
                        <div className="btnsidebyside">
                            {/* <button
                                className="btn buy-now"
                                onClick={handleBuyNow1}
                                disabled={availableStock === 0}
                            >
                                BUY NOW
                            </button> */}
                            <button
                                className="btn buy-now"
                                onClick={handleBuyNow1}
                                disabled={isOutOfStock}
                            >
                                {isOutOfStock ? "OUT OF STOCK" : "BUY NOW"}
                            </button>
                            {/* <button className="btn monthly-plan" onClick={handleAddToCart} disabled={loadingUser}>
                                ADD TO CART
                            </button> */}
                            <button
                                className="btn monthly-plan"
                                onClick={handleAddToCart}
                                disabled={loadingUser || isOutOfStock}
                            >
                                {isOutOfStock ? "ADD TO CART" : "ADD TO CART"}
                            </button>
                        </div>

                        <button
                            className="btn monthly-plan"
                            onClick={() => {
                                const message = `Hi, I'm interested in "${product.productName}". Can you tell me more?`;
                                const url = `https://api.whatsapp.com/send?phone=917204429777&text=${encodeURIComponent(
                                    message
                                )}`;
                                window.open(url, "_blank");
                            }}
                        >
                            CHAT WITH US
                        </button>
                    </div>

                    {/* <p className="product-description">
                        {product.detailedDescription || "No description available."}
                    </p> */}

                    <p className="footer-contact">
                        Any Questions? Please feel free to reach us at: <strong>7204429777</strong>
                    </p>
                </div>
            </div>

            {/* DETAILS (collapsible) */}
            <div className="product-extra-section container">
                <div className="details-grid">
                    <div className="details-left1">
                        {/* DESCRIPTION (above PRODUCT DETAILS) */}
                             <div className="description-block" style={{ marginBottom: 12 }}>
                               <h4 style={{ margin: "0 0 6px 0" }} className="description-title">DESCRIPTION</h4>
                                    <p          ref={descRef}
                                      className={`product-description ${showFullDesc ? "" : "clamped"}`}        >
                                {product.shortDescription || "No description available."}
                                    </p>
                                {descOverflows && (
                                      <button type="button"
                                   className="readmore-btn"
                                    onClick={() => setShowFullDesc((v) => !v)}
                                    aria-expanded={showFullDesc}          >
                                    {showFullDesc ? "Read less" : "Read more"}
                                  </button>
      )}
                          </div>

                        <div
                            className="collapsible-header"
                            onClick={() => setShowDetails((v) => !v)}
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                cursor: "pointer",
                                borderBottom: "1px solid #ddd",
                                paddingBottom: "8px",
                                marginBottom: "10px",
                            }}
                        >
                            <h4 style={{ margin: 0 }}>PRODUCT DETAILS</h4>
                            {showDetails ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </div>

                        {showDetails && (
                            <div className="details-left">
                                <section className="detail-block">
                                    <h4>PRODUCT DETAILS</h4>
                                    <div className="row">
                                        <span className="label">Product Code</span>
                                        <span className="value link">{product.sku || "—"}</span>
                                    </div>
                                    <div className="row">
                                        <span className="label">Category</span>
                                        <span className="value">{product.category || "—"}</span>
                                    </div>
                                    <div className="row">
                                        <span className="label">Collection</span>
                                        <span className="value">{product.collection || "—"}</span>
                                    </div>
                                </section>

                                <section className="detail-block">
                                    <h4>METAL DETAILS</h4>
                                    <div className="row">
                                        <span className="label">Metal</span>
                                        <span className="value">{product.metal || "—"}</span>
                                    </div>
                                    <div className="row">
                                        <span className="label">Purity</span>
                                        <span className="value">{product.purity || "—"}</span>
                                    </div>
                                    {selectedVariant?.weight ? (
                                        <div className="row">
                                            <span className="label">Weight</span>
                                            <span className="value">{selectedVariant.weight}</span>
                                        </div>
                                    ) : null}
                                </section>
                            </div>
                        )}
                    </div>

                    <div className="details-right">
                        {/* <div className="ic">
                            <div className="promise-container">
                                <h4>AVNII PROMISE</h4>
                                <div className="promise-items">
                                    {promiseItems.map((item, index) => (
                                        <div className="promise-item" key={index}>
                                            <span className="promise-icon">{item.icon}</span>
                                            <span className="promise-text">{item.text}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div> */}

                        <div className="certificate">
                            <h4>CERTIFICATE OF AUTHENTICITY</h4>
                            <p>
                                Every piece of silver jewellery we craft is hallmarked by the BIS (Bureau of Indian Standards). This ensures the highest standards of purity and authenticity for your collection.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </>
    );
}
